<template>
	<!-- Under the preview, for the reason OverlayControl records: this is a
	-- control you judge by LOOKING at the picture, so it belongs beside the
	-- picture rather than three groups down a settings panel. -->
	<div class="flex flex-col items-center gap-4 w-full">

		<!-- HOW BIG, and it is the first question rather than the second.
		-- A vector has no size of its own, so this is the only thing the
		-- page really asks. It was not asked at all until now: the drawing
		-- was rasterised at twice its nominal size and the download then
		-- capped at 1280, so an icon came back 48px wide with no way up and
		-- a large diagram lost two thirds of what had been drawn.
		--
		-- BESIDE THE PICTURE, not in the settings panel, for the reason the
		-- note below gives and for one learned since: the panel arrives
		-- folded on a task page, and the rotate controls spent months in
		-- there being reported as missing. A control a page exists for does
		-- not go behind "Show advanced options".
		--
		-- Buttons rather than a menu, like the sheet's layout row: there
		-- are five, the effect is the whole point, and a menu would cost a
		-- click and cover the picture while it was open. -->
		<div class="flex flex-col items-center gap-2 w-full">
			<span class="text-[13px] text-ink-soft">{{ t('options.svgWidth') }}</span>
			<div class="flex flex-wrap items-center justify-center gap-2"
				role="radiogroup" :aria-label="t('options.svgWidth')">
				<button v-for="px in SVG_WIDTHS" :key="px" type="button"
					data-svg-width :data-svg-width-px="px" role="radio"
					:aria-checked="(uiStore.svgWidth || 0) === px ? 'true' : 'false'"
					@click.prevent="setWidth(px)"
					class="btn btn-xs rounded-lg"
					:class="(uiStore.svgWidth || 0) === px ? 'btn-primary' : 'btn-outline'">
					{{ px ? px + ' px' : t('options.svgWidthAuto') }}
				</button>
			</div>
			<!-- What the automatic choice actually came out as. A button
			-- reading "Automatic" tells nobody what they are getting, and
			-- the number is the thing they came for. -->
			<span v-if="shownSize" class="text-[12px] text-ink-mute">{{ shownSize }}</span>
		</div>

		<div class="flex flex-col items-center gap-2 w-full">
		<span class="text-[13px] text-ink-soft">{{ t('options.svgBackground') }}</span>
		<div class="flex flex-wrap items-center justify-center gap-2">
			<button v-for="c in SVG_BACKGROUNDS" :key="c.key"
				data-svg-bg
				:data-svg-bg-key="c.key"
				@click.prevent="pick(c.hex)"
				:title="t('options.svgBackgroundPick', { name: t('colours.' + c.key) })"
				:aria-label="t('options.svgBackgroundPick', { name: t('colours.' + c.key) })"
				:aria-pressed="(uiStore.svgBackground || '') === c.hex ? 'true' : 'false'"
				class="h-7 w-7 rounded-full border-2 transition"
				:class="[
					(uiStore.svgBackground || '') === c.hex ? 'border-white scale-110' : 'border-white/40',
					c.hex ? '' : 'bg-checker',
				]"
				:style="c.hex ? {backgroundColor: c.hex} : null"></button>
		</div>
		</div>
	</div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { SVG_BACKGROUNDS, SVG_WIDTHS } from "../lib/svgRaster"
import { useUiElements } from "../store/uiElements"

const props = defineProps({
	// What the staged PNG actually measures, so "Automatic" can say so.
	size: {type: Object, default: null},
})

const emit = defineEmits(['change'])
const uiStore = useUiElements()
const { t } = useI18n()

const shownSize = computed(() => {
	const s = props.size
	return (s && s.w > 0 && s.h > 0) ? `${s.w} \u00d7 ${s.h}` : ""
})

/* One gesture, so it asks for the work straight away -- the width is drawn
 * INTO the PNG, so this is a re-rasterise rather than a rewritten URL, exactly
 * like the background below. */
function setWidth(px) {
	uiStore.svgWidth = px
	emit('change')
}

/* One gesture, so it asks for the work straight away -- the same split
 * OverlayControl uses, where only the slider waits for `change`. The work
 * here is a re-rasterise rather than a rewritten URL, because the background
 * is painted into the PNG. */
function pick(hex) {
	uiStore.svgBackground = hex
	emit('change')
}
</script>

<style scoped>
/* TRANSPARENT HAS TO LOOK LIKE SOMETHING. A swatch with no fill is an empty
 * circle, which reads as a disabled control rather than as "no background" --
 * so it gets the checkerboard every image editor uses for the same idea.
 * Drawn in CSS rather than as an asset: two gradients weigh nothing and need
 * no request, and tests/test_palette.py refuses a raw hex in a .vue file,
 * which is why these are palette tokens. */
.bg-checker {
	background-image:
		linear-gradient(45deg, var(--ink-soft) 25%, transparent 25%, transparent 75%, var(--ink-soft) 75%),
		linear-gradient(45deg, var(--ink-soft) 25%, transparent 25%, transparent 75%, var(--ink-soft) 75%);
	background-size: 8px 8px;
	background-position: 0 0, 4px 4px;
	background-color: var(--paper);
}
</style>
